const replaceSymbols = {
  "\\r": "\n",
  "</u>": "<u>"
}

const TAGS = {
  SUP_OPEN: "<sup>",
  SUP_CLOSE: "</sup>",
  SUB_OPEN: "<sub>",
  SUB_CLOSE: "</sub>",
};

function replaceAllSymbols(text = '') {
  let newString = text;

  for (const key in replaceSymbols) {
    newString = newString.replaceAll(key, replaceSymbols[key]);
  }

  return newString;
}

function isTag(text, index, tag) {
  const slice = text.slice(index, index + tag.length);
  return slice.toLowerCase() === tag;
}

function extractColorFromTag(tagContent = '') {
  const styleMatch = tagContent.match(/style\s*=\s*['"]?([^'">]+)['"]?/i);
  if (!styleMatch || !styleMatch[1]) {
    return null;
  }

  const styleValue = styleMatch[1].trim();
  const hexInStyle = styleValue.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})\b/i);
  if (hexInStyle) {
    return hexInStyle[0];
  }

  if (/^#?[0-9a-f]{3}(?:[0-9a-f]{3})?(?:[0-9a-f]{2})?$/i.test(styleValue)) {
    return styleValue.startsWith('#') ? styleValue : `#${styleValue}`;
  }

  return null;
}

function replaceSupportedEntities(input = '') {
  return input.replace(/&nbsp;?/gi, '\u00A0');
}

export default function parseText(input, default_color, selectedChars = null, target = null, { preserveWhitespace = false } = {}) {
  if (!input) {
    return {text: "", styles: {}, superscriptRanges: [], subscriptRanges: []};
  }

  const styles = {};
  const superscriptRanges = [];
  const subscriptRanges = [];

  let charIndex = 0;
  let plainIndex = 0;
  let lineIndex = 0;
  let lineStyle = {};
  let text = "";

  let scriptType = null;
  let scriptStart = null;
  let inListItem = false;
  let previousTokenWasTag = false;

  const styleState = {
    underline: false,
    bold: false,
    color: default_color,
  };

  const headingStack = [];

  const appendChar = (char) => {
    if (char === "\n") {
      styles[lineIndex] = lineStyle;
      lineStyle = {};
      lineIndex += 1;
      charIndex = 0;
      text += char;
      plainIndex += 1;
      return;
    }

    const charStyle = {
      fill: styleState.color || default_color,
      underline: styleState.underline,
    };

    if (styleState.bold) {
      charStyle.fontWeight = 'bold';
    }

    if (selectedChars?.length && target && target.get('styles') && target.get('styles')[lineIndex]) {
      const targetCharStyles = target.get('styles')[lineIndex][charIndex] || {};

      if (targetCharStyles.fill) {
        charStyle.fill = targetCharStyles.fill;
      }

      if (targetCharStyles.fontFamily) {
        charStyle.fontFamily = targetCharStyles.fontFamily;
      }
    }

    lineStyle[charIndex] = charStyle;
    charIndex += 1;
    text += char;
    plainIndex += 1;
  };

  const appendText = (value = '') => {
    for (const char of value) {
      appendChar(char);
    }
  };

  // force to convert to string as it was failing for numbers
  input = replaceSupportedEntities(replaceAllSymbols(input + ""));

  let i = 0;
  while (i < input.length) {
    if (isTag(input, i, TAGS.SUP_OPEN)) {
      scriptType = "sup";
      scriptStart = plainIndex;
      i += TAGS.SUP_OPEN.length;
      continue;
    }

    if (isTag(input, i, TAGS.SUP_CLOSE)) {
      if (scriptType === "sup" && scriptStart !== null) {
        superscriptRanges.push([scriptStart, plainIndex]);
      }
      scriptType = null;
      scriptStart = null;
      i += TAGS.SUP_CLOSE.length;
      continue;
    }

    if (isTag(input, i, TAGS.SUB_OPEN)) {
      scriptType = "sub";
      scriptStart = plainIndex;
      i += TAGS.SUB_OPEN.length;
      continue;
    }

    if (isTag(input, i, TAGS.SUB_CLOSE)) {
      if (scriptType === "sub" && scriptStart !== null) {
        subscriptRanges.push([scriptStart, plainIndex]);
      }
      scriptType = null;
      scriptStart = null;
      i += TAGS.SUB_CLOSE.length;
      continue;
    }

    const char = input[i];
    if (char !== '<') {
      // Ignore formatting-only whitespace between tags so `ul><li` and `ul>\n<li` behave the same.
      if (!preserveWhitespace && previousTokenWasTag && /\s/.test(char)) {
        let j = i;
        while (j < input.length && /\s/.test(input[j])) {
          j += 1;
        }

        if (input[j] === '<') {
          i = j;
          continue;
        }
      }

      appendChar(char);
      previousTokenWasTag = false;
      i += 1;
      continue;
    }

    const end = input.indexOf('>', i);
    if (end === -1) {
      appendChar(char);
      previousTokenWasTag = false;
      i += 1;
      continue;
    }

    const fullTag = input.slice(i, end + 1);
    const tagContent = fullTag.slice(1, -1).trim();
    const lowerTag = tagContent.toLowerCase();

    if (lowerTag === 'u' || lowerTag === '/u') {
      styleState.underline = !styleState.underline;
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === 'strong' || lowerTag === 'bold' || lowerTag === 'b') {
      styleState.bold = true;
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === '/strong' || lowerTag === '/bold' || lowerTag === '/b') {
      styleState.bold = false;
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag.startsWith('c ')) {
      const colorMatch = tagContent.match(/['"]([^'"]+)['"]/);
      if (colorMatch?.[1]) {
        styleState.color = colorMatch[1];
      }
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (/^br\s*\/?$/i.test(lowerTag)) {
      appendChar('\n');
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === 'li') {
      inListItem = true;
      if (text && !text.endsWith('\n')) {
        appendChar('\n');
      }
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === '/li') {
      inListItem = false;
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === 'ul' || lowerTag === '/ul') {
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag.startsWith('h1')) {
      if (!inListItem && text && !text.endsWith('\n')) {
        appendChar('\n');
      }

      headingStack.push({
        color: styleState.color,
        bold: styleState.bold,
      });

      const headingColor = extractColorFromTag(tagContent);
      if (headingColor) {
        styleState.color = headingColor;
      }

      styleState.bold = true;
      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    if (lowerTag === '/h1') {
      const previousHeadingStyles = headingStack.pop();
      if (previousHeadingStyles) {
        styleState.color = previousHeadingStyles.color;
        styleState.bold = previousHeadingStyles.bold;
      }

      if (!inListItem && text && !text.endsWith('\n')) {
        appendChar('\n');
      }

      previousTokenWasTag = true;
      i = end + 1;
      continue;
    }

    previousTokenWasTag = true;
    i = end + 1;
  }

  styles[lineIndex] = lineStyle;

  return {text, styles, superscriptRanges, subscriptRanges};
}
